import {WidgetData} from '../../../core/shared/widget/widget-data';
import {WidgetFactory} from '../../../core/shared/widget/service/widget.factory';
import {WidgetDataParam} from '../../../core/shared/widget/widget-data-param';
import {FilterCriteria} from '../../../core/shared/filter/api/filter.criteria';
import {Sorting} from '../../../core/shared/misc/sorting';
import {QlQueryDto} from '../../../core/shared/ql/data/dto/ql-query.dto';
import {buildCwCuratedWidgetForm} from './cw-widget-form.util';

const WIDGET_ALIAS = 'config.cw.CwCaseTypeExchangeFieldDefinition.widget';

/**
 * Builds the "Zusatzfelder" config table widget for cw.CwCaseTypeExchangeFieldDefinition (design G.5
 * node id `exchangeFieldDefinitions`) — the ADDITIONAL export fields of a CaseType. Read-mostly
 * inspection table: rows come from the SMART-Seed bundle, never hand-added in the UI
 * (wp4-fe-ruckfragen-resolved.md Q7).
 *
 * CW-EXCHANGE-V2 (design §3.3) turned the former wire-field registry into exactly this: pass-through
 * fields are no longer configured at all (the whole case graph reaches the template as the export
 * model, generic fields already keyed `gen_<alias>`), so a row only exists for a value that has to be
 * INVENTED — a constant or a value computed by a logic block. `objectLevel` says on which nodes the
 * value is produced and `fieldKey` is the name the skeleton reads it under; the XML placement columns
 * are gone with the binding engine.
 *
 * WidgetFactory.createWidgetTableQl on an explicit, hand-curated QlQueryDto (root
 * cw.CwCaseTypeExchangeFieldDefinition aliased `e`, LEFT joins to the owning caseType and to the
 * computing logic block — each selecting only their display NAME) plus a curated WidgetForm for
 * explicit German column headers. The `objectLevel`/`dataType` enum columns are selected as plain
 * fields — the backend resolves their German labels via @UiEnumValueInfo metadata, no FE enum mirror
 * is needed for rendering. Sorted by `orderIndex` (the deterministic evaluation order), not
 * alphabetically.
 *
 * @param caseTypeId optional cw.CwCaseType id to scope the table to one CaseType (e.g. embedded
 *   under that CaseType's node/detail); omit for the unscoped, all-CaseTypes inspection table.
 */
export function buildCwCaseTypeExchangeFieldDefinitionTableWidget(caseTypeId?: number): WidgetData {
    const qlQuery: QlQueryDto = {
        name: WIDGET_ALIAS,
        type: 'view',
        start: {
            name: 'cw.CwCaseTypeExchangeFieldDefinition',
            as: 'e',
            type: 'entity',
            fields: [
                {name: 'id'},
                {name: 'fieldKey'},      // Feldschlüssel
                {name: 'name'},          // Name
                {name: 'objectLevel'},   // Objektebene (Enum)
                {name: 'constantValue'}, // Konstante (XOR mit Logik)
                {name: 'dataType'},      // Datentyp (Enum)
                {name: 'formatPattern'}, // Formatmuster
                {name: 'orderIndex'},    // Reihenfolge-Index
                {name: 'active'},        // Aktiv
            ],
            joins: [
                {
                    type: 'entity', name: 'caseType', as: 'ct', joinType: 'left',
                    fields: [{name: 'id'}, {name: 'name'}], // Vorgangstyp (resolved NAME)
                },
                {
                    type: 'entity', name: 'logic', as: 'lg', joinType: 'left',
                    fields: [{name: 'id'}, {name: 'name'}], // Logik (resolved NAME)
                },
            ],
        },
        fixedFilters: caseTypeId != null
            ? [FilterCriteria.create('e.caseTypeId', FilterCriteria.cOperatorEqual, caseTypeId)]
            : [],
        // Evaluation order within a level, not alphabetical.
        sortings: [new Sorting('e.orderIndex', true)],
    };

    /**
     * AI:
     * Status: "in progress"
     * Type: Widget
     * SubType: Runtime
     * Reason: CwCaseTypeExchangeFieldDefinition config table — the additional (constant/computed)
     *   export fields per CaseType, curated columns (Vorgangstyp, Feldschlüssel, Name, Objektebene,
     *   Konstante, Logik, Datentyp, Formatmuster, Reihenfolge-Index, Aktiv).
     */
    const widgetData = WidgetFactory.createWidgetTableQl(
        WIDGET_ALIAS,
        'Zusatzfelder',
        qlQuery,
        WidgetDataParam.create('widgetForm', buildCwCaseTypeExchangeFieldDefinitionWidgetForm()),
    );
    widgetData.noDataText = 'Keine Zusatzfelder konfiguriert';
    return widgetData;
}

function buildCwCaseTypeExchangeFieldDefinitionWidgetForm() {
    return buildCwCuratedWidgetForm([
        {field: 'ct.name', uiLabel: 'Vorgangstyp'},
        {field: 'e.fieldKey', uiLabel: 'Feldschlüssel'},
        {field: 'e.name', uiLabel: 'Name'},
        {field: 'e.objectLevel', uiLabel: 'Objektebene'},
        {field: 'e.constantValue', uiLabel: 'Konstante'},
        {field: 'lg.name', uiLabel: 'Logik'},
        {field: 'e.dataType', uiLabel: 'Datentyp'},
        {field: 'e.formatPattern', uiLabel: 'Formatmuster'},
        {field: 'e.orderIndex', uiLabel: 'Reihenfolge-Index'},
        {field: 'e.active', uiLabel: 'Aktiv'},
    ]);
}
